/**
 * Dwelly Listing Data Converter
 * Converts new RESO / DDF / TRREB MLS Web API response format to Dwelly's standard board listing format (board_16 / board_2).
 */

export function getVal(val, defaultVal = "") {
  if (val == null) return defaultVal;
  if (Array.isArray(val)) {
    return val.length > 0 ? (val[0] ?? defaultVal) : defaultVal;
  }
  if (typeof val === "string") {
    return val.trim() || defaultVal;
  }
  return val ?? defaultVal;
}

export function getVal2(val, defaultVal = "") {
  if (val == null) return defaultVal;
  if (Array.isArray(val)) {
    return val.length > 1 ? (val[1] ?? defaultVal) : defaultVal;
  }
  return defaultVal;
}

export function formatAddressFromParts(addr) {
  if (!addr || typeof addr !== "object") return "";
  return [
    addr.unitNumber ? `#${addr.unitNumber}` : "",
    addr.streetNumber,
    addr.streetName,
    addr.streetSuffix,
    addr.city,
    addr.state,
    addr.zip,
  ]
    .filter(Boolean)
    .join(" ");
}

export function isNewRawListingFormat(data) {
  if (!data || typeof data !== "object") return false;
  // If it already has board_16 or board_2, it's the old wrapper format
  if (data.board_16 || data.board_2 || data.board_86 || data.board_17) return false;
  // If it has RESO / DDF fields like StandardStatus, PropertyType, ListPrice, MlsNumber, mlsNumber
  return Boolean(
    data.StandardStatus ||
    data.MlsStatus ||
    data.PropertyType ||
    data.TransactionType ||
    data.ListPrice ||
    data.MlsNumber ||
    data.mlsNumber ||
    data.StreetName ||
    data.PublicRemarks
  );
}

export function convertRawToOldFormat(propertyData, propertyId = "") {
  console.log({ propertyData })
  const raw = propertyData || {};
  const mlsId = raw.MlsNumber || raw.mlsNumber || propertyId;

  const standardStatus = getVal(raw.StandardStatus);
  const mlsStatus = getVal(raw.MlsStatus);
  const propType = getVal(raw.PropertyType);
  const txType = getVal(raw.TransactionType);

  const parsedListPrice = raw.ListPrice ? parseFloat(raw.ListPrice).toFixed(2) : "0.00";
  const parsedOrigPrice = raw.OriginalListPrice
    ? parseFloat(raw.OriginalListPrice).toFixed(2)
    : (raw.ListPrice ? parseFloat(raw.ListPrice).toFixed(2) : "0.00");
  const parsedSoldPrice = raw.ClosePrice
    ? parseFloat(raw.ClosePrice).toFixed(2)
    : "0.00";

  // Calculate synthetic images array if raw.images isn't populated but totalImages > 0
  let images = [];
  if (Array.isArray(raw.images) && raw.images.length > 0) {
    images = raw.images;
  } else if (raw.totalImages && raw.totalImages > 0 && mlsId) {
    images = Array.from({ length: raw.totalImages }, (_, i) => `IMG-${mlsId}_${i + 1}.jpg`);
  }

  const addressObj = {
    area: getVal(raw.Area),
    city: getVal(raw.City),
    country: getVal(raw.Country, "Canada"),
    district: getVal(raw.MunicipalityDistrict) || getVal(raw.City),
    majorIntersection: getVal(raw.CrossStreet),
    neighborhood: getVal(raw.Community),
    streetDirection: getVal(raw.StreetDirPrefix),
    streetName: raw.StreetName || "",
    streetNumber: raw.StreetNumber || "",
    streetSuffix: getVal(raw.StreetSuffix),
    unitNumber: raw.UnitNumber || raw.ApartmentNumber || "",
    zip: getVal(raw.PostalCode),
    state: getVal(raw.StateOrProvince, "Ontario"),
    communityCode: getVal(raw.CommunityCode),
    streetDirectionPrefix: getVal(raw.StreetDirPrefix) || null,
  };

  const fullAddress = formatAddressFromParts(addressObj);

  const property = {
    _id: mlsId || raw.id || (raw.MlsNumber || raw.mlsNumber),
    mlsNumber: mlsId,
    resource: "Property",
    status: standardStatus === "Active" ? "A" : (mlsStatus ? mlsStatus.charAt(0).toUpperCase() : "A"),
    class: (propType && propType.includes("Condo")) ? "CondoProperty" : "Freehold",
    type: (txType === "For Lease") ? "Lease" : "Sale",
    listPrice: parsedListPrice,
    listDate: raw.OriginalEntryTimestamp || new Date().toISOString(),
    lastStatus: mlsStatus || "New",
    soldPrice: parsedSoldPrice,
    soldDate: raw.CloseDate || null,
    originalPrice: parsedOrigPrice,
    assignment: "",
    address: addressObj,
    address_full: fullAddress,
    map: {
      latitude: raw.latitude ? String(raw.latitude) : "",
      longitude: raw.longitude ? String(raw.longitude) : "",
      point: (raw.longitude && raw.latitude) ? `POINT (${raw.longitude} ${raw.latitude})` : "",
    },
    permissions: {
      displayAddressOnInternet: raw.PermissionToContactListingBrokerToAdvertise ? "Y" : "N",
      displayPublic: "Y",
      displayInternetEntireListing: "Y",
    },
    images,
    photoCount: raw.totalImages || images.length,
    details: {
      airConditioning: getVal(raw.Cooling),
      basement1: getVal(raw.Basement, "None"),
      basement2: getVal2(raw.Basement, ""),
      centralVac: raw.CentralVacuumYN ? "Y" : "",
      den: (raw.RoomsTotal && raw.RoomsTotal > (raw.BedroomsTotal || 0)) ? "Y" : "N",
      description: raw.PublicRemarks || "",
      elevator: "",
      exteriorConstruction1: getVal(raw.ConstructionMaterials),
      exteriorConstruction2: getVal2(raw.ConstructionMaterials, ""),
      extras: raw.Extras || "",
      furnished: getVal(raw.Furnished, "N"),
      garage: getVal(raw.ParkingFeatures),
      heating: getVal(raw.HeatTypeMulti),
      numBathrooms: String(raw.BathroomsTotalInteger || 0),
      numBathroomsPlus: raw.MainLevelBathrooms && raw.BathroomsTotalInteger
        ? parseInt(raw.BathroomsTotalInteger) - parseInt(raw.MainLevelBathrooms)
        : null,
      numBedrooms: String(raw.BedroomsTotal || 0),
      numBedroomsPlus: raw.BedroomsPlus ? String(raw.BedroomsPlus) : "",
      numFireplaces: (raw.FireplaceFeatures && raw.FireplaceFeatures.length > 0) ? "Y" : "N",
      numGarageSpaces: raw.ParkingSpaces ? String(raw.ParkingSpaces) : "0",
      numParkingSpaces: raw.ParkingTotal ? String(raw.ParkingTotal) : "0",
      numRooms: String(raw.RoomsTotal || 0),
      numRoomsPlus: raw.RoomsPlus ? String(raw.RoomsPlus) : "",
      patio: "",
      propertyType: propType,
      sqft: getVal(raw.LotSizeArea),
      style: getVal(raw.BuildingStyle) || raw.ArchitecturalStyle || "",
      swimmingPool: getVal(raw.PoolFeatures) || null,
      virtualTourUrl: raw.VirtualTourURLUnbranded || "",
      yearBuilt: raw.YearBuilt || "",
      landAccessType: null,
      landSewer: null,
      viewType: getVal(raw.View) || null,
      zoningDescription: raw.Zoning || null,
      analyticsClick: null,
      moreInformationLink: null,
      alternateURLVideoLink: null,
      flooringType: null,
      foundationType: null,
      landscapeFeatures: null,
      fireProtection: null,
      roofMaterial: getVal(raw.Roof) || null,
      farmType: null,
      zoningType: raw.ZoningDesignation || null,
      businessType: null,
      businessSubType: null,
      landDisposition: null,
      storageType: null,
      constructionStyleSplitLevel: null,
      constructionStatus: null,
      loadingType: null,
      ceilingType: null,
      liveStreamEventURL: null,
      energuideRating: null,
      amperage: null,
      sewer: getVal(raw.Sewer) || null,
      familyRoom: raw.FamilyRoomYN ? "Y" : "N",
      zoning: raw.Zoning || "",
      driveway: null,
      leaseTerms: "",
      centralAirConditioning: "",
      certificationLevel: "",
      energyCertification: "",
      parkCostMonthly: "",
      commonElementsIncluded: "",
      greenPropertyInformationStatement: "",
      handicappedEquipped: "",
      laundryLevel: getVal(raw.LaundryFeatures),
      balcony: "None",
      numKitchens: String(raw.KitchensTotal || 1),
      numKitchensPlus: raw.KitchensPlus || "",
      sqftRange: null,
      numDrivewaySpaces: null,
      HOAFee: raw.AssociationFee ? String(raw.AssociationFee) : null,
      HOAFee2: null,
      HOAFee3: null,
      waterSource: getVal(raw.WaterSource) || null,
      livingAreaMeasurement: null,
      waterfront: raw.WaterfrontYN ? "Y" : null,
      bathrooms: {
        "1": { level: "", count: "1", pieces: "4" },
        "2": { level: "", count: "", pieces: "" },
        "3": { level: "", count: "", pieces: "" },
        "4": { level: "", count: "", pieces: "" },
        "5": { level: "", count: "", pieces: "" },
      },
      numBathroomsHalf: raw.BathroomsHalf ? String(raw.BathroomsHalf) : null,
    },
    daysOnMarket: String(raw.DaysOnMarket || 0),
    occupancy: raw.PossessionDetails || "Immediate",
    updatedOn: raw.SystemModificationTimestamp || raw.OriginalEntryTimestamp || new Date().toISOString(),
    condominium: {
      ammenities: raw.PropertyFeatures || [],
      buildingInsurance: "",
      condoCorp: "",
      condoCorpNum: "",
      exposure: "",
      lockerNumber: "",
      locker: "",
      parkingType: "",
      pets: (raw.PetsAllowed && raw.PetsAllowed.length > 0) ? "restricted" : "N",
      propertyMgr: raw.PropertyManagementCompany || "",
      stories: "",
      fees: {
        cableInlc: "",
        heatIncl: "",
        hydroIncl: "",
        maintenance: raw.AssociationFee ? String(raw.AssociationFee) : "",
        parkingIncl: "",
        taxesIncl: "",
        waterIncl: "",
      },
      lockerUnitNumber: "",
      ensuiteLaundry: "",
      sharesPercentage: "",
      lockerLevel: "",
      unitNumber: raw.UnitNumber || "",
    },
    coopCompensation: raw.TransactionBrokerCompensation || "",
    lot: {
      acres: null,
      depth: raw.LotDepth || null,
      irregular: null,
      legalDescription: raw.TaxLegalDescription || null,
      measurement: getVal(raw.LotSizeArea) || null,
      width: raw.LotFront || null,
      size: null,
    },
    nearby: {
      ammenities: [],
    },
    office: {
      brokerageName: raw.ListOfficeName || "TRREB",
    },
    openHouse: {
      "1": { date: "", endTime: "", startTime: "", type: null, status: null, TZ: "ET" },
      "2": { date: "", endTime: "", startTime: "", type: null, status: null, TZ: "ET" },
      "3": { date: "", endTime: "", startTime: "", type: null, status: null, TZ: "ET" },
    },
    rooms: {
      "1": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "2": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "3": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "4": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "5": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "6": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "7": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "8": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "9": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "10": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "11": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
      "12": { description: "", features: "", features2: "", features3: "", length: "", width: "", level: "" },
    },
    taxes: {
      annualAmount: raw.TaxAnnualAmount ? String(raw.TaxAnnualAmount) : "",
      assessmentYear: raw.TaxYear ? String(raw.TaxYear) : "",
    },
    timestamps: {
      idxUpdated: null,
      listingUpdated: raw.SystemModificationTimestamp || raw.OriginalEntryTimestamp || new Date().toISOString(),
      photosUpdated: raw.PhotosChangeTimestamp || new Date().toISOString(),
      conditionalExpiryDate: null,
      terminatedDate: raw.TerminatedDate || null,
      suspendedDate: raw.SuspendedDate || null,
      listingEntryDate: raw.OriginalEntryTimestamp || new Date().toISOString(),
      closedDate: raw.SoldEntryTimestamp || null,
      unavailableDate: raw.UnavailableDate || null,
      expiryDate: raw.ExpirationDate || new Date().toISOString(),
      extensionEntryDate: null,
      possessionDate: raw.PossessionDate || null,
      repliersUpdatedOn: raw.SystemModificationTimestamp || raw.OriginalEntryTimestamp || new Date().toISOString(),
    },
    agents: [
      {
        agentId: raw.ListAgentMlsId || "",
        boardAgentId: null,
        officeId: null,
        updatedOn: raw.OriginalEntryTimestamp || new Date().toISOString(),
        name: raw.ListAgentFullName || raw.ListAgentName || "",
        board: null,
        boardOfficeId: null,
        position: "Broker",
        email: raw.ListAgentEmail || null,
        phones: [],
        social: [],
        website: raw.ListAgentURL || null,
        photo: { small: null, large: null, updatedOn: raw.OriginalEntryTimestamp || new Date().toISOString() },
        brokerage: {
          name: raw.ListOfficeName || "",
          address: { address1: "", address2: "", city: null, state: null, postal: null, country: null },
        },
      },
    ],
    history: raw.history || [],
    comparables: raw.comparables || [],
    boardId: "16", // Hardcode boardId to 16 for TRREB, matches the check in PropertyDetailsPageV2
    totalImages: raw.totalImages || images.length,
  };
  console.log({ property }) 
  return property;
}

export async function fetchListingDetails(mlsNum) {
  if (!mlsNum) return null;
  try {
    const cleanMls = String(mlsNum).trim();
    const res = await fetch(`/api__/api/listings/${cleanMls}`);
    const liveData = await res.json();

    if (isNewRawListingFormat(liveData)) {
      return convertRawToOldFormat(liveData, cleanMls);
    }

    return (
      liveData.board_16 ||
      liveData.board_2 ||
      liveData.board_86 ||
      liveData.board_17 ||
      Object.values(liveData).find(
        (v) => v && typeof v === "object" && (v.address || v.listPrice || v.price)
      ) ||
      null
    );
  } catch (e) {
    console.error("MLS fetch error:", e);
    return null;
  }
}
